import {
    ActionType, NetworkActionPrefix, PerformanceIndicatorPollStatus, PerformanceIndicatorPollValueAggregationType, PerformanceIndicatorPollValueType, RequestStatus
} from '../utils/constants';
import { deepAssign } from '../utils';
import defaults from '../api/defaults';
import {
    combineReducers, networkActionReducer, networkEntityAddReducer, networkEntityDeleteReducer, networkEntityEditReducer, networkEntityLoadReducer, networkObjectLoadReducer,
    networkPaginationLoadReducer, pipeReducers, synchronizableEnumReducer
} from '../utils/reducerFactory';

function pollAnswered(state, action) {
    switch (action.type) {
        case ActionType.ADD_POLL_RESPONSES_SUCCESS:
            const respondedPolls = action.responses.map(response => response.PollIdentifier).flatten().unique();
            const newState = state.Data.filter(poll => !respondedPolls.includes(poll.PollIdentifier));

            return { ...state, Data: newState };

        case ActionType.IGNORE_POLL_BATCH_USER_SUCCESS:
            return {
                ...state,
                Data: state.Data.filter(poll => poll.PollIdentifier !== action.pollBatchUserID)
            };

        default:
            return state;
    }
}

function scaleChanges(state, action) {
    const pollSettingsScale = state.Data ? state.Data.slice() : [];

    switch (action.type) {
        case ActionType.ADD_POLL_SCALE_VALUES_SUCCESS:
            for (let i = 0; i < action.scaleValues.length; i++) {
                const scaleValue = { ...action.scaleValues[i], ID: action.response[i] };
                pollSettingsScale.push(scaleValue);
            }

            return { ...state, Data: pollSettingsScale };

        case ActionType.EDIT_POLL_SCALE_VALUES_SUCCESS:
            for (let i = 0; i < action.scaleValues.length; i++) {
                pollSettingsScale[pollSettingsScale.getIndexByID(action.scaleValues[i].ID)] = action.scaleValues[i];
            }

            return { ...state, Data: pollSettingsScale };

        case ActionType.DELETE_POLL_SCALE_VALUES_SUCCESS:
            const newState = pollSettingsScale.filter(scale => !action.scaleIDs.includes(scale.ID));
            return { ...state, Data: newState };
        default:
            return state;
    }
}

function pollChanges(state, action) {
    switch (action.type) {
        case ActionType.DELETE_POLL_SETTINGS_SUCCESS: {
            return { ...state,
                pollSettingsScale: deepAssign(state.pollSettingsScale, {
                    [action.pollSettingsID]: {
                        ...defaults.NetworkArrayOf(defaults.PollSettingsScale),
                        Custom: { status: 'SUCCEEDED' }
                    }
                }),
                pollSettings: deepAssign(defaults.PerformanceIndicatorPollSettings, { Custom: { status: 'SUCCEEDED' } }) };
        }

        case ActionType.CLEAR_CURRENT_POLL_PERIOD_AND_SEND_SUCCESS:
            const index = action.settingID ? state.pollSettingsList.Data.getIndexByID(action.settingID) : -1;

            return { ...state,
                pollAnswers: deepAssign(state.pollAnswers, { [action.settingID]: { ...defaults.PollAnswersNetworkArray } }),
                pollGeneralAnswers: deepAssign(state.pollGeneralAnswers, { [action.settingID]: { ...defaults.PollAnswersNetworkArray } }),
                pollSettingsList: deepAssign(state.pollSettingsList, {
                    Data: {
                        [index]: {
                            Custom: {
                                lastAnsweredPollBatch: { ...defaults.PerformanceIndicatorPollBatch }
                            }
                        }
                    }
                }) };

        default:
            return state;
    }
}

function index(state, action, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

const keyVariables = {
        pollSettingID: (state, action) => action.pollSettingID,
        pollSettingsIndex: (state, action) => {
            if (action.type === `${NetworkActionPrefix.LOAD_POLL_SETTINGS_PREFIX}_SUCCESS` ||
            action.type === `${NetworkActionPrefix.LOAD_POLL_SETTINGS_PREFIX}_FAIL` ||
            action.type === `${NetworkActionPrefix.LOAD_POLL_SETTINGS_PREFIX}_PENDING`) {
                const index = state.indexOf(state.find(item => item.ID === action.settingID));
                return index !== -1 ? index : state.length;
            }
            if (!action.pollSettingsID) {
                return undefined;
            }
            return state.indexOf(state.getByID(action.pollSettingsID)) !== -1 ? state.indexOf(state.getByID(action.pollSettingsID)) : state.length;
        },
        decisionPollIndex: (state, action) => index(state, action, action.decisionPollID)
    },

    polls = pipeReducers(
        combineReducers({
            pollSettingsList: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_POLL_SETTINGS_LIST_PREFIX, defaults.NetworkArray),
                combineReducers({
                    Data: combineReducers({
                        '@pollSettingsIndex': pipeReducers(
                            networkEntityLoadReducer(NetworkActionPrefix.LOAD_POLL_SETTINGS_PREFIX, defaults.PerformanceIndicatorPollSettingsLightReport),
                            (state, action) => {
                                if (action.type === ActionType.LOAD_POLL_SETTINGS_PENDING) {
                                    return { ...defaults.PerformanceIndicatorPollSettingsLightReport, ...state, ID: action.settingID };
                                }
                                if (action.type === ActionType.LOAD_POLL_SETTINGS_SUCCESS && !action.response) {
                                    return {
                                        ...defaults.PerformanceIndicatorPollSettingsLightReport,
                                        ID: action.settingID,
                                        Custom: {
                                            status: RequestStatus.SUCCEEDED
                                        }
                                    };
                                }
                                return state;
                            },
                            networkEntityAddReducer(NetworkActionPrefix.ADD_POLL_SETTINGS_PREFIX, defaults.PerformanceIndicatorPollSettingsLightReport, (state, action) => action.settings),
                            networkEntityEditReducer(NetworkActionPrefix.EDIT_POLL_SETTINGS_PREFIX, defaults.PerformanceIndicatorPollSettingsLightReport, (state, action) => action.settings),
                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_POLL_SETTINGS_PREFIX),
                            combineReducers({
                                Batches: networkPaginationLoadReducer(NetworkActionPrefix.LOAD_POLL_BATCH_SLICE_PREFIX, defaults.NetworkPaginationSlice),
                                Custom: combineReducers({
                                    lastAnsweredPollBatch: networkActionReducer(NetworkActionPrefix.LOAD_LAST_ANSWERED_POLL_BATCH_PREFIX)
                                })
                            })
                        )
                    }, keyVariables)
                }),
                (state, action) => {
                    const index = action.pollSettingID ? state.Data.getIndexByID(action.pollSettingID) : -1,
                        settings = index >= 0 ? state.Data[index] : null;

                    switch (action.type) {
                        case ActionType.DELETE_PERFORMANCE_INDICATOR_SUCCESS:
                            return deepAssign(state, {
                                Data: state.Data.filter(settings => settings.IndicatorID !== action.indicatorID)
                            });

                        case ActionType.LOAD_LAST_ANSWERED_POLL_BATCH_PENDING:
                            if (!settings) {
                                return state;
                            }

                            return deepAssign(state, {
                                Data: {
                                    [index]: {
                                        Custom: {
                                            lastAnsweredPollBatch: { Custom: { status: RequestStatus.PENDING } }
                                        }
                                    }
                                }
                            });

                        case ActionType.LOAD_LAST_ANSWERED_POLL_BATCH_SUCCESS:
                            if (!settings) {
                                return state;
                            }

                            return deepAssign(state, {
                                Data: {
                                    [index]: {
                                        Custom: {
                                            lastAnsweredPollBatch: action.response ? action.response : { Custom: { status: RequestStatus.SUCCEEDED } }
                                        }
                                    }
                                }
                            });

                        case ActionType.LOAD_LAST_ANSWERED_POLL_BATCH_FAIL:
                            if (!settings) {
                                return state;
                            }

                            return deepAssign(state, {
                                Data: {
                                    [index]: {
                                        Custom: {
                                            lastAnsweredPollBatch: { Custom: { status: RequestStatus.FAILED } }
                                        }
                                    }
                                }
                            });

                        default:
                            return state;
                    }
                }
            ),
            pollSettingsScale: pipeReducers(
                combineReducers({
                    '@pollSettingID': pipeReducers(
                        networkObjectLoadReducer(NetworkActionPrefix.LOAD_POLL_SCALE_VALUES_PREFIX, defaults.NetworkArrayOf(defaults.PollSettingsScale)),
                        scaleChanges
                    )
                }, keyVariables)
            ),
            pollValueAggregationTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_POLL_VALUE_AGGREGATION_TYPES_PREFIX, defaults.NetworkArrayOf(defaults.PollValueAggregationType), PerformanceIndicatorPollValueAggregationType, 'PerformanceIndicatorPollValueAggregationType'),
            pollValueTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_POLL_VALUE_TYPES_PREFIX, defaults.NetworkArrayOf(defaults.PollValueType), PerformanceIndicatorPollValueType, 'PerformanceIndicatorPollValueType'),
            pollStatuses: synchronizableEnumReducer(NetworkActionPrefix.LOAD_POLL_STATUSES_PREFIX, defaults.NetworkArrayOf(defaults.PerformanceIndicatorPollStatus), PerformanceIndicatorPollStatus, 'PerformanceIndicatorPollStatus'),
            unrespondedPolls: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_UNRESPONDED_POLLS_PREFIX, defaults.NetworkArrayOf(defaults.UnrespondedPolls)),
                (state, action) => {
                    if ((action.type === 'ADD_INSIGHT' || action.type === 'DELETE_INSIGHT') && action.payload.status === RequestStatus.SUCCEEDED && action.payload.entity.Poll) {
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });
                    }

                    return state;
                },
                pollAnswered
            ),
            pollAnswers: pipeReducers(
                combineReducers({
                    '@pollSettingID': pipeReducers(
                        networkPaginationLoadReducer(NetworkActionPrefix.LOAD_PERFORMANCE_INDICATOR_POLL_BATCH_USERS_FROM_TIME_PERIOD_PREFIX, defaults.PollAnswersNetworkArray),
                        (state, action) => {
                            state = state || defaults.PollAnswersNetworkArray;
                            if (action.type === `${NetworkActionPrefix.LOAD_PERFORMANCE_INDICATOR_POLL_BATCH_USERS_FROM_TIME_PERIOD_PREFIX}_SUCCESS`) {
                                return deepAssign(state, { Custom: { loadedPeriods: state.Custom.loadedPeriods.slice().concat([action.startDate.getTime()]) } });
                            }
                            return state;
                        }
                    )
                }, keyVariables)
            ),
            pollGeneralAnswers: pipeReducers(
                combineReducers({
                    '@pollSettingID': pipeReducers(
                        networkPaginationLoadReducer(NetworkActionPrefix.LOAD_PERFORMANCE_INDICATOR_POLL_GENERAL_RESPONSE_FROM_TIME_PERIOD_PREFIX, defaults.PollAnswersNetworkArray),
                        (state, action) => {
                            state = state || defaults.PollAnswersNetworkArray;
                            if (action.type === `${NetworkActionPrefix.LOAD_PERFORMANCE_INDICATOR_POLL_GENERAL_RESPONSE_FROM_TIME_PERIOD_PREFIX}_SUCCESS`) {
                                return deepAssign(state, { Custom: { loadedPeriods: state.Custom.loadedPeriods.slice().concat([action.startDate.getTime()]) } });
                            }
                            return state;
                        }
                    )
                }, keyVariables)
            ),
            unansweredPollRemindersSending: networkActionReducer(NetworkActionPrefix.SEND_REMINDERS_TO_UNANSWERED_POLL_USERS_PREFIX, defaults.NetworkObject),
            notificationSendingTimeTypes: networkObjectLoadReducer(NetworkActionPrefix.LOAD_NOTIFICATION_SENDING_TIME_TYPES_PREFIX, defaults.NetworkArrayOf(defaults.NotificationSendingTimeType)),
            decisionPollReplyOptionTypes: networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DECISION_POLL_REPLY_OPTION_TYPES_PREFIX, defaults.NetworkArrayOf(defaults.DecisionPollReplyOptionTypeReport)),
            decisionPolls: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DECISION_POLLS_PREFIX, defaults.NetworkArray),
                combineReducers({
                    Data: combineReducers({
                        '@decisionPollIndex': pipeReducers(
                            networkEntityAddReducer(NetworkActionPrefix.ADD_DECISION_POLL_PREFIX, null, (state, action) => action.decisionPoll),
                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DECISION_POLL_PREFIX),
                            combineReducers({
                                Respondents: pipeReducers(
                                    networkActionReducer(NetworkActionPrefix.ADD_ALL_DECISION_POLL_RESPONDENTS_PREFIX, null, (state, action) => action.pollRespondents),
                                    (state, action) => {
                                        switch (action.type) {
                                            case ActionType.ADD_ALL_DECISION_POLL_RESPONDENTS_SUCCESS:
                                                return [...state, ...action.pollRespondents.map((item, index) => ({ ...item, ID: action.response[index] }))];

                                            default:
                                                return state;
                                        }
                                    }
                                )
                            })
                        )
                    }, keyVariables)
                }),
                (state, action) => {
                    if (action.type === 'ADD_INSIGHT' && action.payload.status === RequestStatus.SUCCEEDED && action.payload.entity.Poll) {
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });
                    }

                    if (action.type === 'DELETE_INSIGHT' && action.payload.status === RequestStatus.SUCCEEDED) {
                        return deepAssign(state, { Data: state.Data.filter(({ InsightID }) => InsightID !== action.payload.entity.ID) });
                    }

                    return state;
                }
            )
        }),
        pollChanges,
        (state, action) => {
            switch (action.type) {
                case ActionType.VOID_CORPORATE_STRUCTURE:
                case ActionType.DELETE_PRODUCT_SUCCESS:
                case ActionType.DELETE_GEO_AREA_SUCCESS:
                case ActionType.DELETE_CUSTOMER_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_1_ITEM_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_2_ITEM_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_3_ITEM_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_4_ITEM_SUCCESS:
                case ActionType.ADD_MOOD_INDEX_POLL_SUCCESS:
                // TODO make it more targeted, not all parts require update
                    return polls(undefined, { type: '@@redux/INIT' });
                default:
                    return state;
            }
        }
    );

export default polls;
